<ng-template #utilityBarForm>
  <placement-utility-header [placementData]="placementData" [placementMaintenance]="placementMaintenance"></placement-utility-header>
</ng-template>

<div class="tw:p-3 tw:pb-[60px] tw:sm:p-5 tw:sm:px-7 tw:sm:pb-10 tw:bg-[#ffffff] tw:min-h-full tw:box-border">

  @if (ilp) {

  <!-- Back link -->
  <a class="back-link tw:inline-flex tw:items-center tw:gap-1.5 tw:text-xs tw:mb-3.5 tw:cursor-pointer" (click)="backToProfile()">
    <mat-icon class="tw:!w-4 tw:!h-4 tw:!text-base tw:!leading-4">chevron_left</mat-icon>
    Back to learner profile
  </a>

  <!-- Page header -->
  <div class="tw:flex tw:items-start tw:justify-between tw:mb-[18px]">
    <h1 class="page-title">Individual learning plan</h1>
    <div class="tw:flex tw:items-center tw:gap-2">
      <span class="tw:text-[#999] tw:text-xs">Status:</span>
      <!-- Same status -> colour/icon map as the session calendar tile and add-session-v2. -->
      <app-session-status-pill [status]="ilp.status"></app-session-status-pill>
      @if (canDebug) {
        <button mat-stroked-button type="button" class="tw:!text-[12px]" (click)="openDebugDialog()">Debug</button>
      }
    </div>
  </div>

  <mat-card appearance="outlined" class="tw:overflow-hidden">
    <div class="tw:p-4 tw:sm:px-5">

      <!-- Title row -->
      <div class="tw:relative tw:flex tw:flex-wrap tw:items-center tw:justify-center tw:gap-2 tw:pb-3.5 tw:border-b tw:border-b-[#eee]">
        <span class="tw:text-xs tw:text-[#888] tw:sm:absolute tw:sm:left-0 tw:w-full tw:sm:w-auto tw:text-center tw:order-first tw:sm:order-none">{{ ilp.date | date:'dd/MM/yyyy' }}</span>
        <span class="tw:text-[16px] tw:font-semibold tw:text-[#222]">Individual learning plan</span>
      </div>

      <!-- Two-column meta: Academic assessment | SEMH assessment -->
      <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-6 tw:mt-4">

        <!-- Academic assessment -->
        <div class="tw:flex tw:flex-col tw:gap-4">
          <div class="section-label">Academic assessment</div>

          <div class="tw:flex tw:flex-col tw:gap-1.5">
            <label class="field__label" for="ilp-initial-key-stage">Initial key stage</label>
            <select id="ilp-initial-key-stage"
                    class="field__control tw:w-full"
                    [(ngModel)]="ilp.academic.initialKeyStage"
                    [ngModelOptions]="{standalone: true}"
                    [disabled]="isLocked">
              <option [ngValue]="null">Select a key stage</option>
              <option *ngFor="let keyStage of keyStages" [ngValue]="keyStage.id">{{ keyStage.description }}</option>
            </select>
            @if (keyStageMissing && !isLocked) {
              <span class="field__hint tw:text-[#c00] tw:text-[10px] tw:font-semibold">* required to submit</span>
            }
          </div>

          <div class="tw:flex tw:flex-col tw:gap-1">
            <span class="tw:text-xs tw:font-semibold tw:text-[#333]">Initial assessment score %</span>
            <leap-v2-scale-slider [(value)]="ilp.academic.initialScorePct"
                                  [min]="0" [max]="100"
                                  display="percent"
                                  [labels]="percentTicks"
                                  [disabled]="isLocked">
            </leap-v2-scale-slider>
          </div>

          <div class="tw:flex tw:flex-col tw:gap-1">
            <span class="tw:text-xs tw:font-semibold tw:text-[#333]">Target assessment score %</span>
            <leap-v2-scale-slider [(value)]="ilp.academic.targetScorePct"
                                  [min]="0" [max]="100"
                                  display="percent"
                                  [labels]="percentTicks"
                                  [disabled]="isLocked">
            </leap-v2-scale-slider>
          </div>

          <div class="tw:flex tw:flex-col tw:gap-1.5 tw:mt-1">
            <label class="field__label">Assessment used</label>
            <input type="text"
                   class="field__control tw:w-full"
                   [(ngModel)]="ilp.academic.assessmentUsed"
                   [ngModelOptions]="{standalone: true}"
                   [readonly]="isLocked"
                   [maxlength]="assessmentUsedMaxChars"
                   placeholder="Describe how you assessed the learner" />
            <span class="field__hint tw:block tw:text-right">{{ ilp.academic.assessmentUsed.length }} / {{ assessmentUsedMaxChars }}</span>
          </div>
        </div>

        <!-- SEMH assessment -->
        <div class="tw:flex tw:flex-col tw:gap-4 tw:md:border-l tw:md:border-l-[#eee] tw:md:pl-6 tw:border-t tw:border-t-[#eee] tw:pt-4 tw:md:border-t-0 tw:md:pt-0">
          <div class="section-label">SEMH assessment</div>

          <div class="tw:flex tw:flex-col tw:gap-1">
            <span class="tw:text-xs tw:font-semibold tw:text-[#333]">Confidence</span>
            <leap-v2-scale-slider [(value)]="ilp.semh.confidence" [min]="semhMin" [max]="semhMax" [labels]="semhLabels" [colourRamp]="true" [disabled]="isLocked"></leap-v2-scale-slider>
          </div>
          <div class="tw:flex tw:flex-col tw:gap-1">
            <span class="tw:text-xs tw:font-semibold tw:text-[#333]">Resilience</span>
            <leap-v2-scale-slider [(value)]="ilp.semh.resilience" [min]="semhMin" [max]="semhMax" [labels]="semhLabels" [colourRamp]="true" [disabled]="isLocked"></leap-v2-scale-slider>
          </div>
          <div class="tw:flex tw:flex-col tw:gap-1">
            <span class="tw:text-xs tw:font-semibold tw:text-[#333]">Independence</span>
            <leap-v2-scale-slider [(value)]="ilp.semh.independence" [min]="semhMin" [max]="semhMax" [labels]="semhLabels" [colourRamp]="true" [disabled]="isLocked"></leap-v2-scale-slider>
          </div>
          <div class="tw:flex tw:flex-col tw:gap-1">
            <span class="tw:text-xs tw:font-semibold tw:text-[#333]">Motivation</span>
            <leap-v2-scale-slider [(value)]="ilp.semh.motivation" [min]="semhMin" [max]="semhMax" [labels]="semhLabels" [colourRamp]="true" [disabled]="isLocked"></leap-v2-scale-slider>
          </div>
          <div class="tw:flex tw:flex-col tw:gap-1">
            <span class="tw:text-xs tw:font-semibold tw:text-[#333]">Self-regulation</span>
            <leap-v2-scale-slider [(value)]="ilp.semh.selfRegulation" [min]="semhMin" [max]="semhMax" [labels]="semhLabels" [colourRamp]="true" [disabled]="isLocked"></leap-v2-scale-slider>
          </div>
        </div>

      </div><!-- /two-column meta -->

      <!-- Areas of strength -->
      <div class="tw:mt-5">
        <label class="field__label">Areas of strength</label>
        <textarea class="field__control tw:w-full"
                  [(ngModel)]="ilp.areasOfStrength"
                  [ngModelOptions]="{standalone: true}"
                  [readonly]="isLocked"
                  rows="4"
                  [maxlength]="areasOfStrengthMaxChars"></textarea>
        <span class="field__hint tw:block tw:text-right">{{ ilp.areasOfStrength.length }} / {{ areasOfStrengthMaxChars }}</span>
      </div>

      <!-- Areas for development -->
      <div class="tw:mt-3">
        <label class="field__label">Areas for development</label>
        <textarea class="field__control tw:w-full"
                  [(ngModel)]="ilp.areasForDevelopment"
                  [ngModelOptions]="{standalone: true}"
                  [readonly]="isLocked"
                  rows="4"
                  [maxlength]="maxChars"></textarea>
        <span class="field__hint tw:block tw:text-right">{{ ilp.areasForDevelopment.length }} / {{ maxChars }}</span>
      </div>

      <!-- Plan to achieve progress -->
      <div class="tw:mt-3">
        <label class="field__label">Plan to achieve progress over the first six weeks</label>
        <textarea class="field__control tw:w-full"
                  [(ngModel)]="ilp.planForProgress"
                  [ngModelOptions]="{standalone: true}"
                  [readonly]="isLocked"
                  rows="4"
                  [maxlength]="planMaxChars"></textarea>
        <span class="field__hint tw:block tw:text-right">{{ ilp.planForProgress.length }} / {{ planMaxChars }}</span>
      </div>

      <!-- Assessment evidence -->
      <div class="tw:mt-3.5">
        <label class="field__label">Assessment evidence</label>
        <!-- Documents already uploaded to this plan. Each opens through the API, which checks the caller may see it.
             A draft's X takes the document off the list; the next save removes it. A locked plan keeps its documents. -->
        @if (shownSavedEvidence.length > 0) {
          <ul class="saved-evidence tw:list-none tw:p-0 tw:mb-2 tw:flex tw:flex-col tw:gap-[5px]">
            @for (f of shownSavedEvidence; track f.url) {
              <li class="tw:text-xs tw:flex tw:items-center tw:gap-2">
                <a href="" (click)="openEvidence($event, f)">{{ f.name }}</a>
                @if (!isLocked) {
                  <button mat-icon-button type="button" class="remove-saved remove-btn tw:!w-6 tw:!h-6 tw:![line-height:24px]"
                    [attr.aria-label]="'Remove ' + f.name" (click)="removeSavedEvidence(f)">
                    <mat-icon>close</mat-icon>
                  </button>
                }
              </li>
            }
          </ul>
        }
        <leap-v2-evidence-upload (filesChanged)="onEvidenceChanged($event)" [disabled]="isLocked"></leap-v2-evidence-upload>
      </div>

    </div>
  </mat-card>

  <!-- Footer actions -->
  <mat-card appearance="outlined" class="tw:mt-4">
    <div class="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2 tw:p-3 tw:sm:px-4">
      <div class="tw:flex tw:flex-wrap tw:gap-2">
        @if (reportFinalised) {
          <!-- Approved is terminal for sign-off: no Save/Submit and no Accept/Reject. Unlock is the way back. -->
          <button *checkFeatureRender="'UnlockIndividualLearningReports'" mat-stroked-button class="tw:flex-1 tw:min-w-20 tw:sm:flex-none" (click)="unlockIlp()">Unlock</button>
        } @else if (reportSubmitted && canAuthorise) {
          <!-- A submitted plan is signed off by whoever holds the ILP approve/reject features: Accept records
               the QA sign-off and makes it visible to the client, Reject returns it to the tutor with a reason.
               Each button is gated on its own feature, and the tutor — who holds neither — falls through to the
               Save/Submit branch below rather than seeing an empty footer. -->
          @if (canReject) {
            <button mat-stroked-button color="warn" class="tw:flex-1 tw:min-w-20 tw:sm:flex-none" (click)="rejectIlp()">Reject</button>
          }
          @if (canApprove) {
            <button mat-raised-button color="primary" class="tw:flex-1 tw:min-w-20 tw:sm:flex-none" (click)="acceptIlp()">Accept</button>
          }
          <button *checkFeatureRender="'UnlockIndividualLearningReports'" mat-stroked-button class="tw:flex-1 tw:min-w-20 tw:sm:flex-none" [disabled]="!isLocked" (click)="unlockIlp()">Unlock</button>
        } @else {
          <!-- Draft or Rejected: the tutor's Save / Submit. A rejected plan is corrected and resubmitted
               directly — there is no unlock step. -->
          <button mat-raised-button color="primary" class="tw:flex-1 tw:min-w-20 tw:sm:flex-none" [disabled]="isLocked" (click)="saveIlp()">Save</button>
          <button mat-raised-button color="primary" class="tw:flex-1 tw:min-w-20 tw:sm:flex-none" [disabled]="isLocked || keyStageMissing" (click)="submitIlp()">Submit</button>
          <button *checkFeatureRender="'UnlockIndividualLearningReports'" mat-stroked-button class="tw:flex-1 tw:min-w-20 tw:sm:flex-none" [disabled]="!isLocked" (click)="unlockIlp()">Unlock</button>
        }
      </div>
      <button mat-stroked-button class="tw:flex-1 tw:min-w-20 tw:sm:flex-none" (click)="backToProfile()">Close</button>
    </div>
  </mat-card>

  } @else {
  <div class="tw:text-[13px] tw:text-[#888] tw:p-4">Loading plan…</div>
  }

</div>
